import React, { Fragment } from 'react';
import ComponentName from '../constant/ComponentName';
import CollectionNames from '../constant/CollectionNames';
import { RuleJsonModel, RuleSchema } from '../domain/collection/rule/FlowDomains';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { RootState } from '../redux/store';
import { changePageFilter } from '../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import RuleService from '../service/RuleService';
import { ComponentState, setSelectedItems } from '../redux/actions/ComponentActions';
import StandardContainerComponent, { StandardSubscriptionComponentArgs } from '../helpers/StandardContainerComponent';
import RuleListViewComponent from '../components/rule/RuleListViewComponent';
import LanguageUtil from '../util/LanguageUtil';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';
import { OverlayPanel } from 'primereact/overlaypanel';
import RoleHelper from 'RoleHelper';
import RuleCreateDropdown from '../components/custom/RuleCreateDropdown';

class RuleContainer extends StandardContainerComponent<Props, State, RuleJsonModel, RuleSchema> {
  private readonly ruleService: RuleService = ServiceContainer.getInstance().get(ServiceNames.FLOW);
  private createOverlayRef: OverlayPanel | null = null;

  constructor(props: Props) {
    const args: StandardSubscriptionComponentArgs = {
      component: ComponentName.RULE,
      withSchema: true,
      itemCollection: CollectionNames.RULES,
      label: LanguageUtil.get({ id: 'pages.rule.title', defaultMessage: 'Rules' }),
    };
    super(props, args);
  }

  public render() {
    return (
      <Fragment>
        {super.render()}
        <OverlayPanel ref={(el) => (this.createOverlayRef = el)}>
          <RuleCreateDropdown 
            onNavigate={this.navigateToCreate}
            onDismiss={() => this.createOverlayRef?.hide()}
            id={this.id}
          />
        </OverlayPanel>
      </Fragment>
    );
  }

  private navigateToCreate = (path: string) => {
    const {
      router: { navigate },
    } = this.props;
    navigate(`${path}/create`);
  };

  protected openCreatePage = (e: any) => {
    if (!RoleHelper.canEdit('Rule')) return;
    this.createOverlayRef?.toggle(e);
  };

  protected getListView(): React.ReactNode {
    const { items, itemBuilderView, permissions } = this.state;
    return (
      <RuleListViewComponent
        rules={items}
        views={itemBuilderView?.getListViews()}
        loading={this.state.loading}
        currentModal={itemBuilderView?.getLastCurrent()}
        openRule={this.openComponent}
        onSortChange={this.onSortChange}
        permissions={permissions}
      />
    );
  }

  private getRuleNamesFromModals() {
    const {
      component: { selectedComponentIds },
    } = this.props;
    return selectedComponentIds.map((rule: RuleJsonModel) => rule.name);
  }

  protected async deployAll(): Promise<BulkActionDependencyError[]> {
    return await this.ruleService.bulkAction(this.getRuleNamesFromModals(), 'Deploy');
  }

  protected async undeployAll(): Promise<BulkActionDependencyError[]> {
    return await this.ruleService.bulkAction(this.getRuleNamesFromModals(), 'Undeploy');
  }

  protected async removeAll(): Promise<BulkActionDependencyError[]> {
    return await this.ruleService.bulkAction(this.getRuleNamesFromModals(), 'Remove');
  }

  protected async prepareCount() {
    const count = await this.ruleService.getCount(this.getCountFilter().build());
    this.setState({ totalRecord: count });
  }

  protected openComponent = (id?: string) => {
    const {
      router: { navigate },
    } = this.props;
    const { items } = this.state;
    if (id != undefined) {
      const rule = items.find((f) => f.name == id);
      if (rule) {
        navigate(`edit/${id}`);
      }
    } else {
      navigate('create');
    }
  };
}

class State {}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  pagePath: state.page.pagePath,
  currentCategory: state.page.currentCategory,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
});
const mapDispatchToProps = { setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(RuleContainer));
